import { api } from '../console/api.ts';
import { empty, field, meter, pageHead, pill } from '../console/components.ts';
import { ApiError, InputError } from '../console/errors.ts';
import {
  agentName,
  CURRENCY_NAME,
  type Cur,
  esc,
  fmt,
  guatemalaMonth,
  icon,
  lineName,
  type Money,
  PRODUCT,
  q,
} from '../console/format.ts';
import { type Actions, type ClickAction, render } from '../console/router.ts';
import { $, act, centsOf } from '../console/ui.ts';
import { optionTag, selectControl, textControl } from '../controls.ts';

/**
 * «Metas e incentivos» (management) and «Mis metas» (an agent): the progress towards the year's
 * goals, the composition of the portfolio, the incentives, and the director's plan editor.
 */

type GoalRow = {
  agentId: string | null;
  productLine: string | null;
  target: Money;
  issued: Money;
  progress: number;
  remaining: Money;
  reached: boolean;
};
type IncentiveRow = {
  name: string;
  agentId: string;
  productLine: string | null;
  threshold: Money;
  reward: string;
  issued: Money;
  progress: number;
  remaining: Money;
  status: 'REACHED' | 'IN_PROGRESS';
};
type GoalsViewBody = {
  year: number;
  agentId: string | null;
  scope: 'AGENCY' | 'OWN' | 'AGENT';
  agents: string[];
  canConfigure: boolean;
  composition: {
    productLine: string;
    currency: Cur;
    policies: number;
    premium: Money;
    share: number;
  }[];
  goals: GoalRow[];
  incentives: IncentiveRow[];
};
type GoalPlanBody = {
  year: number;
  goals: { agentId: string | null; productLine: string | null; target: Money }[];
  incentives: {
    name: string;
    agentId: string | null;
    productLine: string | null;
    threshold: Money;
    reward: string;
  }[];
};
/** A row of the plan editor, as typed (amounts stay text until saved). */
type DraftGoal = { agentId: string; productLine: string; currency: Cur; amount: string };
type DraftIncentive = DraftGoal & { name: string; reward: string };

/** What «Metas» shows: the year and, for management, the agency ('') or one agent. */
let goalsFilter = { year: 0, agent: '' };
/** The director's plan being edited (null: load it from the API). */
let goalDraft: { year: number; goals: DraftGoal[]; incentives: DraftIncentive[] } | null = null;

const GOAL_ERRORS: Record<string, (message: string) => string> = {
  INVALID_GOAL: () => 'Cada meta debe ser un monto mayor que cero.',
  DUPLICATE_GOAL: () =>
    'Hay dos metas para la misma persona, el mismo ramo y la misma moneda; deja solo una.',
  INVALID_INCENTIVE: () =>
    'Cada incentivo necesita nombre, un umbral mayor que cero y el premio que se gana.',
  DUPLICATE_INCENTIVE: () => 'Hay dos incentivos con el mismo nombre; cámbiale el nombre a uno.',
  UNKNOWN_AGENT: () => 'Uno de los agentes ya no es de tu agencia; elige otro.',
  UNKNOWN_PRODUCT_LINE: () => 'Uno de los ramos no existe; elige otro.',
  TOO_MANY_GOALS: () => 'Son demasiadas metas o incentivos para un año.',
};

const pctText = (n: number) => `${n.toLocaleString('es-GT', { maximumFractionDigits: 1 })} %`;
const whoText = (agentId: string | null) => (agentId ? agentName(agentId) : 'Agencia');
const lineText = (l: string | null) => (l ? lineName(l) : 'Todos los ramos');
const amountText = (m: Money) => (m.amountMinor / 100).toFixed(2);
const currentYear = () => Number(guatemalaMonth(new Date()).slice(0, 4));

/** One goal as a tile: how far, a meter, and what is left. */
function goalTile(g: GoalRow, own: boolean) {
  const title = own
    ? g.productLine
      ? `Meta de ${lineName(g.productLine)}`
      : 'Meta anual'
    : `${whoText(g.agentId)} · ${lineText(g.productLine)}`;
  return `<div class="card kpi goal-kpi${g.reached ? ' reached' : ''}"><span class="kpi-label">${icon(g.reached ? 'ok' : 'target')}${esc(title)}</span>
    <span class="kpi-value">${esc(pctText(g.progress))}</span>${meter(g.progress / 100, g.reached ? 'done' : '')}
    <span class="kpi-foot">${fmt(g.issued)} de ${fmt(g.target)} · ${g.reached ? 'meta alcanzada' : `faltan ${fmt(g.remaining)}`}</span></div>`;
}

/** The portfolio issued in the year: one bar per product line, per currency. */
function compositionCard(v: GoalsViewBody) {
  if (!v.composition.length)
    return `<section class="card"><div class="card-head"><h2>Composición de la cartera</h2></div><p class="muted card-pad">Aún no hay pólizas emitidas en ${v.year}.</p></section>`;
  const currencies = [...new Set(v.composition.map((c) => c.currency))];
  const groups = currencies
    .map((cur) => {
      const rows = v.composition.filter((c) => c.currency === cur);
      const total = rows.reduce((s, r) => s + r.premium.amountMinor, 0);
      return `<div class="share-group">${currencies.length > 1 ? `<h3 class="kpi-group">En ${esc(CURRENCY_NAME[cur])} · ${q(total, cur)}</h3>` : ''}
        <ul class="share-list" aria-label="${esc(`Prima emitida por ramo en ${CURRENCY_NAME[cur]}`)}">${rows
          .map(
            (r) => `<li><span class="share-name">${esc(lineName(r.productLine))}</span>
            <span class="share-bar">${meter(r.share / 100)}</span>
            <strong class="share-pct">${esc(pctText(r.share))}</strong>
            <span class="share-amount muted small">${fmt(r.premium)} · ${r.policies === 1 ? '1 póliza' : `${r.policies} pólizas`}</span></li>`,
          )
          .join('')}</ul></div>`;
    })
    .join('');
  return `<section class="card" data-component="portfolio-composition"><div class="card-head"><h2>Composición de la cartera</h2><span class="spacer"></span><span class="muted small">Prima neta emitida en ${v.year}, por ramo</span></div>
    <div class="card-pad share-groups">${groups}</div></section>`;
}

function goalsTable(goals: GoalRow[]) {
  return `<section class="card"><div class="card-head"><h2>Metas de los agentes</h2></div><div class="table-wrap" data-component="table"><table class="table goals-table"><caption class="sr-only">Avance de cada agente hacia sus metas</caption>
    <thead><tr><th>Agente</th><th>Ramo</th><th class="num">Meta</th><th class="num">Emitida</th><th>Avance</th><th class="num">Falta</th></tr></thead><tbody>
    ${goals
      .map(
        (
          g,
        ) => `<tr><td>${esc(whoText(g.agentId))}</td><td>${esc(lineText(g.productLine))}</td><td class="num">${fmt(g.target)}</td><td class="num">${fmt(g.issued)}</td>
        <td class="nowrap"><strong class="num">${esc(pctText(g.progress))}</strong>${meter(g.progress / 100, `inline${g.reached ? ' done' : ''}`)}</td><td class="num">${g.reached ? pill('ok', 'Alcanzada') : fmt(g.remaining)}</td></tr>`,
      )
      .join('')}</tbody></table></div></section>`;
}

function incentivesCard(v: GoalsViewBody, own: boolean) {
  if (!v.incentives.length) return '';
  return `<section class="card" data-component="incentives"><div class="card-head"><h2>Incentivos</h2><span class="spacer"></span><span class="muted small">Se ganan al emitir el umbral en ${v.year}</span></div>
    <div class="table-wrap" data-component="table"><table class="table goals-table"><caption class="sr-only">Estatus de cada incentivo</caption>
    <thead><tr><th>Incentivo</th>${own ? '' : '<th>Agente</th>'}<th>Ramo</th><th class="num">Umbral</th><th>Avance</th><th>Estatus</th></tr></thead><tbody>
    ${v.incentives
      .map(
        (
          i,
        ) => `<tr><td><strong>${esc(i.name)}</strong><div class="muted small">${esc(i.reward)}</div></td>${own ? '' : `<td>${esc(agentName(i.agentId))}</td>`}
        <td>${esc(lineText(i.productLine))}</td><td class="num">${fmt(i.threshold)}</td>
        <td class="nowrap"><strong class="num">${esc(pctText(i.progress))}</strong>${meter(i.progress / 100, `inline${i.status === 'REACHED' ? ' done' : ''}`)}<div class="muted small">${fmt(i.issued)} emitidos</div></td>
        <td>${i.status === 'REACHED' ? pill('ok', 'Alcanzado') : `${pill('info', 'En curso')}<div class="muted small">faltan ${fmt(i.remaining)}</div>`}</td></tr>`,
      )
      .join('')}</tbody></table></div></section>`;
}

const draftOf = (p: GoalPlanBody) => ({
  year: p.year,
  goals: p.goals.map((g) => ({
    agentId: g.agentId ?? '',
    productLine: g.productLine ?? '',
    currency: g.target.currency,
    amount: amountText(g.target),
  })),
  incentives: p.incentives.map((i) => ({
    name: i.name,
    reward: i.reward,
    agentId: i.agentId ?? '',
    productLine: i.productLine ?? '',
    currency: i.threshold.currency,
    amount: amountText(i.threshold),
  })),
});

/** The director's editor of the year's plan: goals and incentives, row by row. */
function planEditor(year: number, agents: string[]) {
  const d = goalDraft;
  if (!d) return '';
  const opt = optionTag;
  const lines = Object.keys(PRODUCT).filter((l) => l !== 'PROPERTY');
  const whoSelect = (kind: string, i: number, v: string, all: string) =>
    selectControl(
      {
        'data-plan': kind,
        'data-row': i,
        'data-field': 'agentId',
        label: 'Para quién',
        hideLabel: true,
      },
      `${opt('', all, !v)}${agents.map((a) => opt(a, agentName(a), a === v)).join('')}`,
    );
  const lineSelect = (kind: string, i: number, v: string) =>
    selectControl(
      {
        'data-plan': kind,
        'data-row': i,
        'data-field': 'productLine',
        label: 'Ramo',
        hideLabel: true,
      },
      `${opt('', 'Todos los ramos', !v)}${lines.map((l) => opt(l, lineName(l), l === v)).join('')}`,
    );
  const amount = (kind: string, i: number, r: DraftGoal, label: string) =>
    `<span class="goal-amount">${selectControl(
      {
        'data-plan': kind,
        'data-row': i,
        'data-field': 'currency',
        label: `Moneda de ${label}`,
        hideLabel: true,
      },
      `${opt('GTQ', 'Q', r.currency === 'GTQ')}${opt('USD', 'US$', r.currency === 'USD')}`,
    )}
      ${textControl({ 'data-plan': kind, 'data-row': i, 'data-field': 'amount', inputmode: 'decimal', label, hideLabel: true, placeholder: '300,000.00', value: r.amount })}</span>`;
  const remove = (kind: string, i: number, what: string) =>
    `<button type="button" class="btn sm ghost" data-plan-remove="${kind}" data-row="${i}" aria-label="Quitar ${esc(what)}">Quitar</button>`;
  const goalRows = d.goals
    .map(
      (g, i) =>
        `<tr><td>${whoSelect('goal', i, g.agentId, 'Toda la agencia')}</td><td>${lineSelect('goal', i, g.productLine)}</td><td>${amount('goal', i, g, 'Meta')}</td><td class="actions">${remove('goal', i, `la meta ${i + 1}`)}</td></tr>`,
    )
    .join('');
  const incentiveRows = d.incentives
    .map(
      (x, i) =>
        `<tr><td>${textControl({ 'data-plan': 'incentive', 'data-row': i, 'data-field': 'name', label: 'Nombre del incentivo', hideLabel: true, placeholder: 'Bono Autos', maxlength: 80, value: x.name })}</td>
        <td>${whoSelect('incentive', i, x.agentId, 'Cada agente')}</td><td>${lineSelect('incentive', i, x.productLine)}</td><td>${amount('incentive', i, x, 'Umbral')}</td>
        <td>${textControl({ 'data-plan': 'incentive', 'data-row': i, 'data-field': 'reward', label: 'Premio', hideLabel: true, placeholder: 'Bono de Q3,000.00', maxlength: 200, value: x.reward })}</td>
        <td class="actions">${remove('incentive', i, `el incentivo ${i + 1}`)}</td></tr>`,
    )
    .join('');
  return `<section class="card" data-component="goal-plan-editor" aria-labelledby="plan-title"><div class="card-head"><h2 id="plan-title">Definir metas e incentivos de ${year}</h2><span class="spacer"></span><span class="muted small">Prima neta emitida en el año</span></div>
    <div class="card-pad plan-editor">
      <h3>Metas</h3>
      ${d.goals.length ? `<div class="table-wrap"><table class="table plan-table line-editor"><thead><tr><th>Para</th><th>Ramo</th><th>Meta</th><th class="actions"><span class="sr-only">Quitar</span></th></tr></thead><tbody>${goalRows}</tbody></table></div>` : '<p class="muted">Sin metas todavía.</p>'}
      <div><button type="button" class="btn sm" data-plan-add="goal">Agregar meta</button></div>
      <h3>Incentivos</h3>
      ${d.incentives.length ? `<div class="table-wrap"><table class="table plan-table line-editor"><thead><tr><th>Incentivo</th><th>Para</th><th>Ramo</th><th>Umbral</th><th>Premio</th><th class="actions"><span class="sr-only">Quitar</span></th></tr></thead><tbody>${incentiveRows}</tbody></table></div>` : '<p class="muted">Sin incentivos todavía.</p>'}
      <div><button type="button" class="btn sm" data-plan-add="incentive">Agregar incentivo</button></div>
      <div class="toolbar plan-save"><span class="grow muted small">Guardar reemplaza las metas e incentivos de ${year}.</span><button type="button" class="btn primary" id="plan-save" data-plan-save>Guardar metas de ${year}</button></div>
    </div></section>`;
}

/** Reads the editor's inputs back into the draft (before adding, removing or saving rows). */
function syncGoalDraft() {
  if (!goalDraft) return;
  for (const el of document.querySelectorAll<HTMLInputElement | HTMLSelectElement>(
    '[data-plan][data-row][data-field]',
  )) {
    const list = el.dataset.plan === 'goal' ? goalDraft.goals : goalDraft.incentives;
    const row = list[Number(el.dataset.row)] as Record<string, string> | undefined;
    if (row && el.dataset.field) row[el.dataset.field] = el.value;
  }
}

/** The draft as the API takes it; a wrong amount says which row. */
function planBody(): Omit<GoalPlanBody, 'year'> {
  const d = goalDraft;
  if (!d) throw new InputError('No hay metas que guardar.');
  const amountOf = (r: DraftGoal, what: string) => {
    let cents: number | null;
    try {
      cents = centsOf(r.amount, false);
    } catch {
      cents = null;
    }
    if (!cents) throw new InputError(`Escribe ${what}, por ejemplo 300,000.00`);
    return { amountMinor: cents, currency: r.currency };
  };
  return {
    goals: d.goals.map((g, i) => ({
      agentId: g.agentId || null,
      productLine: g.productLine || null,
      target: amountOf(g, `el monto de la meta ${i + 1}`),
    })),
    incentives: d.incentives.map((x, i) => {
      if (!x.name.trim()) throw new InputError(`Escribe el nombre del incentivo ${i + 1}.`);
      if (!x.reward.trim()) throw new InputError(`Escribe el premio de «${x.name.trim()}».`);
      return {
        name: x.name.trim(),
        agentId: x.agentId || null,
        productLine: x.productLine || null,
        threshold: amountOf(x, `el umbral de «${x.name.trim()}»`),
        reward: x.reward.trim(),
      };
    }),
  };
}

export async function metas() {
  const year = goalsFilter.year || currentYear();
  const query = new URLSearchParams({ year: String(year) });
  if (goalsFilter.agent) query.set('agent', goalsFilter.agent);
  const v = await api<GoalsViewBody>('GET', `/v1/goals?${query}`);
  const own = v.scope === 'OWN';
  if (v.canConfigure && goalDraft?.year !== year)
    goalDraft = draftOf(await api<GoalPlanBody>('GET', `/v1/goal-plans/${year}`));
  const years = [currentYear() + 1, currentYear(), currentYear() - 1, currentYear() - 2];
  const opt = optionTag;
  const head = pageHead(
    own ? 'Mis metas' : 'Metas e incentivos',
    own
      ? `Tu avance hacia las metas de ${year}, la composición de tu cartera y el estatus de tus incentivos.`
      : `El avance de la agencia y de cada agente hacia las metas de ${year}, la composición de la cartera y los incentivos.`,
  );
  const filters = `<form class="card toolbar stats-filters" id="goals-filters" aria-label="Año y agente">
    ${field('Año', selectControl({ id: 'gl-year' }, years.map((y) => opt(String(y), String(y), y === year)).join('')))}
    ${v.agents.length ? field('Ver', selectControl({ id: 'gl-agent' }, `${opt('', 'Toda la agencia', !goalsFilter.agent)}${v.agents.map((a) => opt(a, agentName(a), a === goalsFilter.agent)).join('')}`)) : ''}
    <p class="muted small stats-scope">${icon(own ? 'lock' : 'users')}${own ? 'Solo tus metas' : v.agentId ? `Metas de ${esc(agentName(v.agentId))}` : 'Toda la agencia'}</p></form>`;
  const top = v.scope === 'AGENCY' ? v.goals.filter((g) => g.agentId === null) : v.goals;
  const byAgent = v.scope === 'AGENCY' ? v.goals.filter((g) => g.agentId !== null) : [];
  const noGoals = !v.goals.length && !v.incentives.length;
  const tiles = top.length
    ? `<section class="kpis goal-kpis" data-component="goal-progress" aria-label="Avance hacia las metas">${top.map((g) => goalTile(g, own || v.scope === 'AGENT')).join('')}</section>`
    : '';
  const none = noGoals
    ? empty(
        'target',
        `Sin metas para ${year}`,
        v.canConfigure
          ? 'Define abajo las metas de la agencia y de cada agente, y los incentivos del año.'
          : 'Cuando la dirección defina las metas del año, verás aquí tu avance.',
        '',
        2,
      )
    : '';
  return `${head}${filters}${none}${tiles}${compositionCard(v)}${byAgent.length ? goalsTable(byAgent) : ''}${incentivesCard(v, own || v.scope === 'AGENT')}${v.canConfigure && !goalsFilter.agent ? planEditor(year, v.agents) : ''}`;
}

/** Forgets the year, the agent and the plan being edited (a new session). */
export function resetGoals() {
  goalsFilter = { year: 0, agent: '' };
  goalDraft = null;
}

/** Adds or removes a row of the plan editor (what was typed is kept). */
const editPlan: ClickAction = (_, d) => {
  syncGoalDraft();
  if (goalDraft) {
    const kind = d.planAdd ?? d.planRemove;
    const blank = { agentId: '', productLine: '', currency: 'GTQ' as Cur, amount: '' };
    if (d.planAdd === 'goal') goalDraft.goals.push(blank);
    else if (d.planAdd === 'incentive')
      goalDraft.incentives.push({ ...blank, name: '', reward: '' });
    else (kind === 'goal' ? goalDraft.goals : goalDraft.incentives).splice(Number(d.row), 1);
  }
  void render('metas', { quiet: true }).then(() => {
    const rows = document.querySelectorAll<HTMLElement>(
      `[data-plan="${CSS.escape(d.planAdd ?? d.planRemove ?? '')}"]`,
    );
    (d.planAdd ? rows[rows.length - 1] : $(`[data-plan-add="${d.planRemove}"]`))?.focus();
  });
};

/** The year or the agent changed: the goals are read again, with the focus back on the filter. */
function applyGoalsFilter(sel: HTMLSelectElement) {
  if (sel.id === 'gl-year') goalsFilter.year = Number(sel.value);
  else goalsFilter.agent = sel.value;
  void render('metas', { quiet: true }).then(() => $(`#${sel.id}`)?.focus());
}

export const actions: Actions = {
  click: {
    planAdd: editPlan,
    planRemove: editPlan,
    planSave: (t) => {
      void act(t, 'Guardando las metas…', async () => {
        syncGoalDraft();
        const year = goalDraft?.year ?? currentYear();
        const body = planBody();
        try {
          await api('PUT', `/v1/goal-plans/${year}`, body);
        } catch (e) {
          const say = e instanceof ApiError ? GOAL_ERRORS[e.code] : undefined;
          throw say ? new InputError(say(e instanceof ApiError ? e.message : '')) : e;
        }
        goalDraft = null;
        await render('metas', { quiet: true });
        return `Metas de ${year} guardadas: ${body.goals.length === 1 ? '1 meta' : `${body.goals.length} metas`} y ${body.incentives.length === 1 ? '1 incentivo' : `${body.incentives.length} incentivos`}.`;
      });
    },
  },
  change: {
    'gl-year': applyGoalsFilter,
    'gl-agent': applyGoalsFilter,
  },
};
